/**
 * TW — Sample Picker
 *
 * Handles:
 * - Sample variant selection (one per order, replace not stack)
 * - Cart add / swap via Shopify AJAX Cart API
 * - Checkout nudge when picker is visible but no sample selected
 *
 * Lockfile reference: §15
 * Cart line item properties: _tw_role, _tw_scent_title, _tw_scent_handle
 */

(function () {
  'use strict';

  const SAMPLE_ROLE = 'sample';
  const PROP_ROLE = '_tw_role';
  const PROP_SCENT_TITLE = '_tw_scent_title';
  const PROP_SCENT_HANDLE = '_tw_scent_handle';

  // ─── Utility ─────────────────────────────────────────────────────────────────

  /**
   * Derive a URL-safe handle from a variant title.
   * Matches Shopify's standard handle format.
   */
  function titleToHandle(title) {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Fetch the current Shopify cart as JSON.
   */
  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
      return res.json();
    });
  }

  /**
   * Remove a line item from the cart by its line key.
   */
  function removeCartItem(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart remove failed: ' + res.status);
      return res.json();
    });
  }

  /**
   * Add a new sample line item to the cart.
   */
  function addSampleToCart(variantId, variantTitle, variantHandle) {
    var properties = {};
    properties[PROP_ROLE] = SAMPLE_ROLE;
    properties[PROP_SCENT_TITLE] = variantTitle;
    properties[PROP_SCENT_HANDLE] = variantHandle;

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: properties,
      }),
    }).then(function (res) {
      if (!res.ok) {
        return res.json().then(function (err) {
          throw new Error(err.description || 'Cart add failed');
        });
      }
      return res.json();
    });
  }

  // ─── DOM helpers ─────────────────────────────────────────────────────────────

  function getPickerEl() {
    return document.getElementById('tw-sample-picker');
  }

  function getNudgeEl() {
    return document.getElementById('tw-sample-picker-nudge');
  }

  function getAllOptionBtns(picker) {
    return picker.querySelectorAll('.tw-sample-picker__option');
  }

  function getSelectedOption(picker) {
    return picker.querySelector('.tw-sample-picker__option.is-selected');
  }

  function setButtonLoading(btn, isLoading) {
    btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    btn.classList.toggle('is-loading', isLoading);
    // Disable all buttons during in-flight request to prevent double-tap
    var picker = btn.closest('#tw-sample-picker');
    if (picker) {
      getAllOptionBtns(picker).forEach(function (b) {
        if (b !== btn) b.disabled = isLoading;
      });
    }
  }

  function clearNudge() {
    var nudge = getNudgeEl();
    if (nudge) nudge.classList.remove('is-visible');
    var picker = getPickerEl();
    var inner = picker && picker.querySelector('.tw-sample-picker__inner');
    if (inner) inner.classList.remove('is-nudging');
  }

  function showNudge() {
    var nudge = getNudgeEl();
    if (!nudge) return;
    nudge.classList.add('is-visible');

    // Scroll the picker into view, offset for sticky header on mobile
    var picker = getPickerEl();
    if (picker) {
      var header = document.querySelector('header, .site-header, #shopify-section-header');
      var headerHeight = header ? header.offsetHeight : 60;
      var top = picker.getBoundingClientRect().top + window.scrollY - headerHeight - 120;
      window.scrollTo({ top: top, behavior: 'smooth' });
    }

    // Thicker border + pulse on inner container
    var inner = picker && picker.querySelector('.tw-sample-picker__inner');
    if (inner) {
      inner.classList.add('is-nudging');
      inner.classList.remove('tw-sample-picker--pulse');
      void inner.offsetWidth;
      inner.classList.add('tw-sample-picker--pulse');
    }
  }

  // ─── Selection logic ──────────────────────────────────────────────────────────

  /**
   * Handle a sample option button click.
   * 1. Find and remove any existing sample line item
   * 2. Add the new variant with correct line properties
   * 3. Update UI to reflect new selection
   * 4. Dispatch a custom event so the cart section can re-render
   */
  function handleOptionClick(btn) {
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    if (btn.classList.contains('is-unavailable')) return;

    var variantId = parseInt(btn.dataset.variantId, 10);
    var variantTitle = btn.dataset.variantTitle;
    var variantHandle = titleToHandle(variantTitle);

    // If already selected, do nothing (deselection not supported — one per order)
    if (btn.classList.contains('is-selected')) return;

    setButtonLoading(btn, true);
    clearNudge();

    fetchCart()
      .then(function (cart) {
        // Find existing sample key if present
        var existingKey = null;
        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          if (
            item.properties &&
            item.properties[PROP_ROLE] === SAMPLE_ROLE
          ) {
            existingKey = item.key;
            break;
          }
        }

        if (existingKey) {
          return removeCartItem(existingKey).then(function () {
            return addSampleToCart(variantId, variantTitle, variantHandle);
          });
        } else {
          return addSampleToCart(variantId, variantTitle, variantHandle);
        }
      })
      .then(function () {
        // Update button states
        var picker = btn.closest('#tw-sample-picker');
        if (picker) {
          getAllOptionBtns(picker).forEach(function (b) {
            var wasSelected = b === btn;
            b.classList.toggle('is-selected', wasSelected);
            b.setAttribute('aria-pressed', wasSelected ? 'true' : 'false');
            b.disabled = b.classList.contains('is-unavailable');
          });
        }

        // Clear nudge state on any selection
        clearNudge();

        setButtonLoading(btn, false);

        // Notify cart that items changed so Empire theme can update totals
        document.dispatchEvent(new CustomEvent('tw:sample:selected', {
          bubbles: true,
          detail: {
            variantId: variantId,
            variantTitle: variantTitle,
            variantHandle: variantHandle,
          },
        }));

        // Also trigger Empire's standard cart refresh event if present
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(function (err) {
        console.error('[TW Sample Picker] Error:', err);
        setButtonLoading(btn, false);
        // Re-enable buttons on failure; do not mutate selection state
        var picker = btn.closest('#tw-sample-picker');
        if (picker) {
          getAllOptionBtns(picker).forEach(function (b) {
            if (!b.classList.contains('is-unavailable')) {
              b.disabled = false;
            }
          });
        }
      });
  }

  // ─── Checkout nudge ───────────────────────────────────────────────────────────

  /**
   * Intercept checkout button clicks.
   * If picker is visible and no sample selected, scroll to picker and show nudge.
   * Does not block checkout — this is a soft nudge only.
   */
  function attachCheckoutNudge() {
    document.addEventListener('click', function (e) {
      var target = e.target;

      // Match any checkout button/link on the cart page
      var isCheckoutTrigger =
        target.matches('[name="checkout"]') ||
        target.matches('a[href="/checkout"]') ||
        target.closest('[name="checkout"]') ||
        target.closest('a[href="/checkout"]');

      if (!isCheckoutTrigger) return;

      var picker = getPickerEl();
      if (!picker) return;

      // Only nudge if picker is visible (cart qualifies, threshold met)
      var isVisible = picker.offsetParent !== null;
      if (!isVisible) return;

      // Check if a sample is selected
      var selected = getSelectedOption(picker);
      if (selected) return; // Selection made — allow checkout

      // No selection — show nudge and scroll
      e.preventDefault();
      showNudge();
    });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    var picker = getPickerEl();
    if (!picker) return;

    // Bind option buttons
    picker.addEventListener('click', function (e) {
      var btn = e.target.closest('.tw-sample-picker__option');
      if (btn) handleOptionClick(btn);
    });

    // Attach checkout nudge
    attachCheckoutNudge();
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();